import { useEffect } from 'react';
import { eventoSeleccionado } from '../types/types';
import { getLangValue } from '../api/config';


interface Hour {
    code: string;
    hour: string;
    status: string;
}
  
interface Session {
    title: string;
    hour: string;
    date: string;
    hours: Hour[];
  }
  interface SessionWithDateTime {
    date: string;             // "10-01-2025"
    hour?: string | null;     // "15:00 hs." o null
    datetime: Date;           // siempre presente para orden cronológico
    datetimeStr: string;
    hasHour: boolean;         // si vino o no la hora
  }


interface FirstAndLastSession {
    first: SessionWithDateTime | null;
    last: SessionWithDateTime | null;
  }

const EventJsonLd = (eventoSeleccionado:any) => {

    const lang = getLangValue('lang');

  useEffect(() => {
    const scriptId = 'event-jsonld';
    let frontUrl = "";
    let money = "ARS";
    if (lang === 'es_ES') {
        frontUrl = "https://www.entradaweb.es"
        money = "EUR"
    } else {
        frontUrl = "https://www.entradaweb.com.ar/"
    }

    

    // Verificamos si ya existe un script con ese ID
    if (document.getElementById(scriptId)) return;
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    const event = eventoSeleccionado.event;
    const firstSession = getFirstAndLastSession(event);

    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Event",
      "name": event.title?.replace(/<br\/>/g, ' '),
      "description": event.description,
      "startDate": firstSession?.first?.datetimeStr,
      "endDate": firstSession?.last?.datetimeStr,
      "location": {
        "@type": "Place",
        "name": event.place,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": event.address,
          "addressLocality": event.location,
          "addressCountry": event.country
        }
      },
      "offers": {
        "@type": "Offer",
        "url": event.link,
        "availability": "https://schema.org/InStock"
      },
      "eventStatus": "https://schema.org/EventScheduled",
      "image": event.image
    });

    document.head.appendChild(script);
  }, [eventoSeleccionado]);

  function getFirstAndLastSession(data: eventoSeleccionado): FirstAndLastSession {
    if (!Array.isArray(data.sessions)) {
      return { first: null, last: null };
    }
  
    const sessionsWithDateTime: SessionWithDateTime[] = data.sessions.flatMap((session: Session) => {
      const date = session.date;
  
      if (!date) return [];
  
      if (Array.isArray(session.hours) && session.hours.length > 0) {
        // Si hay horas, usar cada una
        return session.hours.map(hourObj => {
          const datetime = parseDateTime(date, hourObj.hour, data.country);
          return {
            date,
            hour: hourObj.hour,
            datetime: datetime.datetime,
            datetimeStr: datetime.datetimeStr,
            hasHour: true
          };
        }).filter(s => s.datetime !== null) as SessionWithDateTime[];
      } else {
        // Si no hay horas, usar fecha con hora 00:00
        const datetime = parseDateTime(date, null, data.country);
        if (!datetime) return [];
        return [{
          date,
          hour: null,
          datetime: datetime.datetime,
          datetimeStr: datetime.datetimeStr,
          hasHour: false
        }];
      }
    });
  
    if (sessionsWithDateTime.length === 0) {
      return { first: null, last: null };
    }
  
    sessionsWithDateTime.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
  
    return {
      first: sessionsWithDateTime[0],
      last: sessionsWithDateTime[sessionsWithDateTime.length - 1]
    };
  }
  
  function parseDateTime(dateStr: string, hourStr: string | null, country: string): any | null {
    if (!dateStr) return null;
  
    const [day, month, year] = dateStr.split('-');
    const time = hourStr ? hourStr.replace(' hs.', '').trim() : '00:00';
    let offset = '-00:00';
    if (country.toLowerCase() === 'argentina') {
      // Ajustar a UTC-3 para Argentina
      offset = '-03:00';
    }
    const datetimeStr = `${year}-${month}-${day}T${time}:00${offset}`;
  
    const date = new Date(datetimeStr);
    
    if (isNaN(date.getTime())) return null;

    return {datetime: date, datetimeStr: datetimeStr};
  }

  return null; // No renderiza nada en el DOM
};
  
export default EventJsonLd;